import React, { useEffect, useState, useCallback, useMemo, useReducer } from 'react';
import type { MenuProps } from 'antd';
import {
    ClockCircleOutlined,
    ExportOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    DownOutlined,
    ArrowLeftOutlined,
    UndoOutlined,
    HistoryOutlined,
    WarningOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { exportV4ScheduleToExcel } from '../../utils/exportScheduleExcel';
import { exportV4ScheduleToPdf } from '../../utils/exportSchedulePdf';

import ScheduleMatrix from './components/ScheduleMatrix';
import ManualEditDrawer from './components/ManualEditDrawer';
import AssignmentsView from './views/AssignmentsView';
import api from '../../services/api';
import {
    WxbButton,
    WxbDrawer,
    WxbDropdown,
    WxbEmpty,
    WxbIcon,
    WxbModal,
    WxbPopconfirm,
    WxbSegmented,
    WxbSpinner,
    WxbTag,
    WxbTooltip,
    WxbAlert,
    wxbToast,
} from '../wxb-ui';
import {
    buildShiftWindow,
    buildV4ManualOverride,
    initialV4ResultEditorState,
    normalizeV4Result,
    resolveCandidateContext,
    selectV4Metrics,
    validateV4WorkingResult,
    V4AssignmentContext,
    V4ShiftAssignment,
    applyV4ResultCommand,
    v4ResultEditorReducer,
} from './resultDomain';

import './SolverV4.css';

interface SolveResultV4PageProps {
    visible: boolean;
    runId: number | null;
    onClose: () => void;
}

const SolveResultV4Page: React.FC<SolveResultV4PageProps> = ({ visible, runId, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [editorState, dispatchEditor] = useReducer(
        v4ResultEditorReducer,
        initialV4ResultEditorState,
    );
    const data = editorState.workingResult;
    const editHistory = editorState.past;
    const [error, setError] = useState<string | null>(null);
    const [applying, setApplying] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [discardingDraft, setDiscardingDraft] = useState(false);
    const [runStatus, setRunStatus] = useState<string>('INIT');

    // Manual edit state
    const [editTarget, setEditTarget] = useState<V4ShiftAssignment | null>(null);

    // Precheck collapsed state
    const [precheckExpanded, setPrecheckExpanded] = useState(false);

    // Edit history drawer
    const [historyVisible, setHistoryVisible] = useState(false);
    const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);

    // Tab state: matrix vs assignments
    const [activeTab, setActiveTab] = useState<'matrix' | 'assignments'>('matrix');

    // External filter signal for AssignmentsView (set by KPI card click)
    const [assignFilterSignal, setAssignFilterSignal] = useState<{
        status: 'ALL' | 'UNASSIGNED' | 'PARTIAL' | 'COMPLETE'; nonce: number;
    } | null>(null);

    const fetchResult = useCallback(async () => {
        if (!runId) return;
        setLoading(true);
        setError(null);
        try {
            const { data: json } = await api.get(`/v4/scheduling/runs/${runId}/result`);

            if (json.success) {
                const rawData = json.data;
                const empInfo = new Map<number, { name: string; code: string }>();

                if (rawData.assignments) {
                    rawData.assignments.forEach((a: any) => {
                        empInfo.set(a.employee_id, { name: a.employee_name, code: a.employee_code });
                    });
                }

                if (rawData.shift_assignments) {
                    rawData.shift_assignments = rawData.shift_assignments.map((s: any) => ({
                        ...s,
                        employee_name: s.employee_name || empInfo.get(s.employee_id)?.name || `员工 ${s.employee_id}`,
                        employee_code: s.employee_code || empInfo.get(s.employee_id)?.code || ''
                    }));
                }

                dispatchEditor({ type: 'LOAD', result: normalizeV4Result(rawData) });
                if (rawData.run_status) setRunStatus(rawData.run_status);
            } else {
                setError(json.message || '加载结果失败');
            }
        } catch (e: any) {
            setError(e.message || '网络错误');
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => {
        if (visible && runId) {
            fetchResult();
        } else {
            dispatchEditor({ type: 'CLEAR' });
            setRunStatus('INIT');
            setApplying(false);
            setSavingDraft(false);
            setDiscardingDraft(false);
            setPrecheckExpanded(false);
        }
    }, [visible, runId, fetchResult]);

    // Date range
    const dateRange = useMemo(() => {
        if (!data?.calendar_days?.length) return undefined;
        const dates = data.calendar_days.map(d => d.date).sort();
        return { start: dates[0], end: dates[dates.length - 1] };
    }, [data]);

    const shiftOptions = useMemo(
        () => (data?.shift_definitions || [])
            .filter((shift) => shift.is_active)
            .sort((a, b) => a.shift_id - b.shift_id),
        [data],
    );

    // ── KPI Metrics (real-time recalculated) ──
    const kpiMetrics = useMemo(() => {
        if (!data) return null;

        const metrics = selectV4Metrics(data);

        // Standalone task stats
        const standaloneOps = (data.operations || []).filter((op: any) => op.batch_code === 'STANDALONE');
        const standaloneTotal = standaloneOps.length;
        const standaloneAssigned = standaloneOps.filter((op: any) => op.status === 'COMPLETE').length;

        // Uncovered / partially covered operation counts
        const uncoveredOps = (data.operations || []).filter((op: any) => op.status === 'UNASSIGNED').length;
        const partialOps = (data.operations || []).filter((op: any) => op.status === 'PARTIAL').length;

        return {
            completion_rate: metrics.completionRate,
            coverage_rate: metrics.coverageRate,
            balanceIndex: metrics.balanceIndex,
            employeeCount: metrics.employeeCount,
            qualityScore: metrics.qualityScore,
            standaloneTotal,
            standaloneAssigned,
            uncoveredOps,
            partialOps,
            totalPositions: metrics.totalPositions,
            assignedPositions: metrics.assignedPositions,
        };
    }, [data]);

    // Handle manual shift edit (with history tracking)
    const handleApplyEdit = useCallback((employeeId: number, date: string, newShiftId: number): boolean => {
        if (!data) return false;
        const newShift = shiftOptions.find(s => s.shift_id === newShiftId);
        if (!newShift) return false;
        const oldShiftData = data.shift_assignments?.find(
            (shift) => shift.employee_id === employeeId && shift.date === date
        );
        if (oldShiftData?.is_locked) {
            wxbToast.error(`该班次已锁定${oldShiftData.lock_reason ? `：${oldShiftData.lock_reason}` : ''}`);
            return false;
        }
        const command = {
            type: 'CHANGE_SHIFT' as const,
            employeeId,
            date,
            shiftId: newShiftId,
        };
        const proposed = applyV4ResultCommand(data, command);
        if (proposed === data) {
            wxbToast.error('没有找到可修改的班次计划');
            return false;
        }
        const affectedErrors = validateV4WorkingResult(proposed).filter((issue) => (
            issue.severity === 'ERROR'
            && issue.employeeId === employeeId
            && (!issue.date || issue.date === date)
        ));
        if (affectedErrors.length > 0) {
            const preview = affectedErrors.slice(0, 3).map((issue) => issue.message).join('；');
            wxbToast.error(`班次修改不满足当前操作约束：${preview}`);
            return false;
        }
        dispatchEditor({
            type: 'EXECUTE',
            label: `${oldShiftData?.employee_name || `员工${employeeId}`} ${date}：${oldShiftData?.shift_name || '无'} → ${newShift.shift_name}`,
            command,
        });
        return true;
    }, [data, shiftOptions]);

    // ── Operation Assignment Edit Handlers ──

    const handleAssign = useCallback((
        opId: number,
        posNum: number,
        empId: number,
        explicitContext?: V4AssignmentContext,
    ): boolean => {
        if (!data) return false;
        const op = data.operations?.find((operation) => operation.operation_plan_id === opId);
        const position = op?.positions?.find((item) => item.position_number === posNum);
        if (!op || position?.is_locked) {
            wxbToast.error('该岗位已锁定，不能修改人员');
            return false;
        }
        const context = explicitContext || resolveCandidateContext(data, empId, op);
        if (!context) {
            wxbToast.error('该人员没有能够完整覆盖操作的工作班次');
            return false;
        }
        const meta = data.employee_meta?.[empId];
        const roster = data.shift_assignments.find((shift) => shift.employee_id === empId);
        const employee = {
            id: empId,
            name: meta?.name || roster?.employee_name || `员工${empId}`,
            code: meta?.code || roster?.employee_code || '',
        };
        const command = {
            type: 'ASSIGN_EMPLOYEE' as const,
            operationId: opId,
            positionNumber: posNum,
            employee,
            context,
        };
        const proposed = applyV4ResultCommand(data, command);
        const affectedErrors = validateV4WorkingResult(proposed).filter((issue) => (
            issue.severity === 'ERROR'
            && issue.operationId === opId
            && issue.positionNumber === posNum
        ));
        if (affectedErrors.length > 0) {
            wxbToast.error(affectedErrors.slice(0, 3).map((issue) => issue.message).join('；'));
            return false;
        }
        dispatchEditor({
            type: 'EXECUTE',
            label: `${op.operation_name} 岗位${posNum}：${position?.employee?.name || '空缺'} → ${employee.name}`,
            command,
        });
        return true;
    }, [data]);

    const handleUnassign = useCallback((opId: number, posNum: number): boolean => {
        if (!data) return false;
        const op = data.operations?.find((operation) => operation.operation_plan_id === opId);
        const pos = op?.positions?.find((position) => position.position_number === posNum);
        if (pos?.is_locked) {
            wxbToast.error('该岗位已锁定，不能移除人员');
            return false;
        }
        dispatchEditor({
            type: 'EXECUTE',
            label: `${op?.operation_name || opId} 岗位${posNum}：移除${pos?.employee?.name || '人员'}`,
            command: {
                type: 'UNASSIGN_EMPLOYEE',
                operationId: opId,
                positionNumber: posNum,
            },
        });
        return true;
    }, [data]);

    const handleAssignWithShiftChange = useCallback((
        opId: number,
        posNum: number,
        empId: number,
        newShiftId: number,
        date: string,
        explicitContext?: V4AssignmentContext,
    ): boolean => {
        if (!data) return false;
        const newShift = shiftOptions.find(s => s.shift_id === newShiftId);
        if (!newShift) return false;

        const empShift = data.shift_assignments?.find((shift) => (
            shift.employee_id === empId && shift.date === date
        ));
        const op = data.operations?.find((operation) => operation.operation_plan_id === opId);
        const oldPos = op?.positions?.find((position) => position.position_number === posNum);
        if (!op || !empShift) {
            wxbToast.error('该人员在目标日期没有可调整的班次计划');
            return false;
        }
        if (oldPos?.is_locked || empShift.is_locked) {
            wxbToast.error('岗位或班次已锁定，不能执行改派调班');
            return false;
        }
        const window = buildShiftWindow(date, newShift);
        const operationStart = dayjs(explicitContext?.planned_start || op.planned_start);
        const operationEnd = dayjs(explicitContext?.planned_end || op.planned_end);
        if (operationStart.isBefore(window.start) || operationEnd.isAfter(window.end)) {
            wxbToast.error(`${newShift.shift_name}不能完整覆盖该操作`);
            return false;
        }
        const meta = data.employee_meta?.[empId];
        const employee = {
            id: empId,
            name: meta?.name || empShift?.employee_name || `员工${empId}`,
            code: meta?.code || empShift?.employee_code || '',
        };
        const command = {
            type: 'ASSIGN_AND_CHANGE_SHIFT' as const,
            operationId: opId,
            positionNumber: posNum,
            employee,
            shiftId: newShiftId,
            context: explicitContext || {
                date,
                shift_id: newShiftId,
                planned_start: op.planned_start,
                planned_end: op.planned_end,
            },
        };
        const proposed = applyV4ResultCommand(data, command);
        const affectedErrors = validateV4WorkingResult(proposed).filter((issue) => (
            issue.severity === 'ERROR'
            && (
                issue.employeeId === empId
                || (issue.operationId === opId && issue.positionNumber === posNum)
            )
        ));
        if (affectedErrors.length > 0) {
            wxbToast.error(affectedErrors.slice(0, 3).map((issue) => issue.message).join('；'));
            return false;
        }
        dispatchEditor({
            type: 'EXECUTE',
            label: `${op.operation_name} 岗位${posNum}：${oldPos?.employee?.name || '空缺'} → ${employee.name}，班次改为${newShift.shift_name}`,
            command,
        });
        return true;
    }, [data, shiftOptions]);

    // Undo last edit
    const handleUndo = useCallback(() => {
        dispatchEditor({ type: 'UNDO' });
    }, []);

    const handleRedo = useCallback(() => {
        dispatchEditor({ type: 'REDO' });
    }, []);

    // Reset all edits
    const handleResetAll = useCallback(() => {
        dispatchEditor({ type: 'RESET' });
    }, []);

    // Edit delta summary
    const editDelta = useMemo(() => {
        if (editHistory.length === 0 || !editorState.baseSnapshot || !data) return null;
        const baseByKey = new Map(editorState.baseSnapshot.shift_assignments.map((shift) => (
            [`${shift.employee_id}:${shift.date}`, shift]
        )));
        let nightDelta = 0;
        let hoursDelta = 0;
        data.shift_assignments.forEach((shift) => {
            const base = baseByKey.get(`${shift.employee_id}:${shift.date}`);
            if (!base || base.shift_id === shift.shift_id) return;
            nightDelta += Number(shift.is_night_shift) - Number(base.is_night_shift);
            hoursDelta += shift.credited_hours - base.credited_hours;
        });
        return { count: editHistory.length, nightDelta, hoursDelta: Math.round(hoursDelta * 10) / 10 };
    }, [data, editHistory.length, editorState.baseSnapshot]);

    // Precheck summary
    const precheckSummary = useMemo(() => {
        if (!data?.precheck_results) return null;
        const { status, checks } = data.precheck_results;
        const issues = checks.filter(c => c.status !== 'PASS');
        return { status, issues, total: checks.length };
    }, [data]);

    const validationIssues = useMemo(
        () => data ? validateV4WorkingResult(data) : [],
        [data],
    );
    const validationErrors = useMemo(
        () => validationIssues.filter((issue) => issue.severity === 'ERROR'),
        [validationIssues],
    );
    const validationWarnings = useMemo(
        () => validationIssues.filter((issue) => issue.severity === 'WARNING'),
        [validationIssues],
    );
    const specialShiftSummary = useMemo(() => {
        if (!data) return null;
        const requirementCount = Number(data.metrics.special_shift_requirement_count || 0);
        const occurrenceCount = Number(data.metrics.special_shift_occurrence_count || 0);
        const requiredHeadcount = Number(data.metrics.special_shift_required_headcount_total || 0);
        const assignedHeadcount = Number(
            data.special_shift_assignments?.length
            || data.metrics.special_shift_assigned_headcount_total
            || 0,
        );
        const shortage = Number(
            data.special_shift_shortages?.reduce(
                (total, item) => total + Number(item.shortage_people || 0),
                0,
            )
            || data.metrics.special_shift_shortage_total
            || 0,
        );
        if (requirementCount === 0 && occurrenceCount === 0 && requiredHeadcount === 0) return null;
        return {
            requirementCount,
            occurrenceCount,
            requiredHeadcount,
            assignedHeadcount,
            shortage,
        };
    }, [data]);

    // 把当前(可能手改过的)结果展平成 apply 接口认识的 assignments / shift_schedule 覆盖载荷。
    // 用于"按当前修改后的结果直接覆盖"——后端据此重写生产表,而非回读求解器原始结果。
    const buildManualOverride = useCallback(
        () => data ? buildV4ManualOverride(data) : null,
        [data],
    );

    const showValidationError = useCallback((items: Array<{ message: string }>) => {
        const preview = items.slice(0, 3).map(item => item.message).join('；');
        wxbToast.error(
            `当前结果有 ${items.length} 项校验错误：${preview}`
            + (items.length > 3 ? `；另有 ${items.length - 3} 条` : '')
        );
    }, []);

    const handleSaveDraft = async () => {
        if (!runId || !data || editHistory.length === 0 || savingDraft) return;
        const manualOverride = buildManualOverride();
        if (!manualOverride) return;
        if (manualOverride.issues.some((issue) => issue.severity === 'ERROR')) {
            showValidationError(manualOverride.issues.filter((issue) => issue.severity === 'ERROR'));
            return;
        }
        setSavingDraft(true);
        try {
            const { data: json } = await api.put(`/v4/scheduling/runs/${runId}/draft`, {
                override_result: manualOverride.payload,
                base_version: data.draft?.version ?? 0,
            });
            if (!json.success) {
                wxbToast.error(json.error || '保存草稿失败');
                return;
            }
            wxbToast.success(`草稿已保存（版本 ${json.data?.version ?? 1}）`);
            await fetchResult();
        } catch (e: any) {
            wxbToast.error(e?.response?.data?.error || '保存草稿失败，请重试');
        } finally {
            setSavingDraft(false);
        }
    };

    const handleDiscardDraft = async () => {
        if (!runId || !data?.draft?.exists || discardingDraft) return;
        setDiscardingDraft(true);
        try {
            const { data: json } = await api.delete(`/v4/scheduling/runs/${runId}/draft`, {
                data: { base_version: data.draft.version },
            });
            if (!json.success) {
                wxbToast.error(json.error || '放弃草稿失败');
                return;
            }
            wxbToast.success('草稿已放弃，已恢复到最近一次应用或求解结果');
            await fetchResult();
        } catch {
            wxbToast.error('放弃草稿失败，请重试');
        } finally {
            setDiscardingDraft(false);
        }
    };

    // Apply result
    const handleApplyResult = async () => {
        if (!runId || applying) return;
        const hasEdits = editHistory.length > 0;
        const hasSavedDraft = Boolean(data?.draft?.exists);
        const manualOverride = hasEdits ? buildManualOverride() : null;
        if (hasEdits && !manualOverride) return;
        setApplying(true);
        try {
            // 有未保存手改时直接应用当前页面版本；只有已存草稿时由后端读取草稿应用。
            // 原始结果且无草稿时仍维持原有无 body 调用。
            const basePayload = hasEdits || hasSavedDraft
                ? {
                    ...(manualOverride ? { override_result: manualOverride.payload, force: true } : {}),
                    base_draft_version: data?.draft?.version ?? 0,
                }
                : undefined;
            const { data: json } = await api.post(`/v4/scheduling/runs/${runId}/apply`, basePayload);
            if (!json.success) {
                wxbToast.error(json.error || '应用失败');
                return;
            }
            const summary = [
                `批次分配 ${json.data?.batch_assignments_inserted ?? 0} 条`,
                `班次 ${json.data?.shift_plans_inserted ?? 0} 条`,
            ];
            wxbToast.success(
                `${hasEdits || hasSavedDraft ? '已按当前结果应用：' : '排班已应用：'}`
                + summary.join('，'),
            );
            setRunStatus('APPLIED');
            await fetchResult();
        } catch (e: any) {
            wxbToast.error(e?.response?.data?.error || '应用失败，请重试');
        } finally {
            setApplying(false);
        }
    };

    const hasUnsavedEdits = editHistory.length > 0;
    const hasSavedDraft = Boolean(data?.draft?.exists);
    const hasPendingChanges = hasUnsavedEdits || hasSavedDraft;
    const requestClose = useCallback(() => {
        if (hasUnsavedEdits) {
            setCloseConfirmVisible(true);
            return;
        }
        onClose();
    }, [hasUnsavedEdits, onClose]);

    const drawerTitle = (
        <div className="v4-result-title">
            <div className="v4-result-title-main">
                <WxbButton
                    variant="ghost"
                    size="sm"
                    aria-label="返回"
                    onClick={requestClose}
                >
                    <ArrowLeftOutlined />
                </WxbButton>
                <span className="v4-result-title-text">排班结果</span>
                {runId && <span className="v4-result-title-run">#{runId}</span>}
                {dateRange && (
                    <span className="v4-result-title-range">
                        {dateRange.start} ~ {dateRange.end}
                    </span>
                )}
                {data?.metrics.solve_time && (
                    <span className="v4-solve-time-badge">
                        <ClockCircleOutlined /> 求解 {data.metrics.solve_time}s
                    </span>
                )}
            </div>
            <div className="v4-result-actions">
                <WxbDropdown
                    menu={{
                        items: [
                            {
                                key: 'excel', icon: <FileExcelOutlined />, label: '导出 Excel',
                                onClick: async () => {
                                    if (data && runId) {
                                        const hide = wxbToast.loading('生成 Excel...');
                                        try {
                                            await exportV4ScheduleToExcel(data, runId, {
                                                hasUnsavedEdits: editHistory.length > 0,
                                                baseSnapshot: editorState.baseSnapshot,
                                                editLabels: editHistory.map((entry) => entry.label),
                                            });
                                            wxbToast.success('Excel 导出成功');
                                        }
                                        catch { wxbToast.error('Excel 导出失败'); }
                                        finally { hide(); }
                                    }
                                }
                            },
                            {
                                key: 'pdf', icon: <FilePdfOutlined />, label: '导出 PDF',
                                onClick: async () => {
                                    if (data && runId) {
                                        const hide = wxbToast.loading('生成 PDF...');
                                        try { await exportV4ScheduleToPdf(data, runId); wxbToast.success('PDF 导出成功'); }
                                        catch { wxbToast.error('PDF 导出失败'); }
                                        finally { hide(); }
                                    }
                                }
                            }
                        ] as MenuProps['items']
                    }}
                >
                    <WxbButton variant="secondary" size="sm">
                        <ExportOutlined /> 导出 <DownOutlined className="v4-export-caret" />
                    </WxbButton>
                </WxbDropdown>

                {hasSavedDraft && (
                    <WxbTag color="blue" title={data?.draft?.saved_at ? `保存于 ${dayjs(data.draft.saved_at).format('YYYY-MM-DD HH:mm:ss')}` : undefined}>
                        草稿 v{data?.draft?.version}
                    </WxbTag>
                )}
                {hasUnsavedEdits && (
                    <WxbTag color="amber">未保存 {editHistory.length} 处</WxbTag>
                )}

                {hasSavedDraft && (
                    <WxbPopconfirm
                        title="确认放弃草稿"
                        description={hasUnsavedEdits
                            ? '已保存草稿和当前未保存修改都会被放弃，是否继续？'
                            : '将恢复到最近一次应用或求解结果，是否继续？'}
                        onConfirm={handleDiscardDraft}
                        okText="放弃草稿"
                        okButtonProps={{ danger: true, loading: discardingDraft }}
                    >
                        <WxbButton variant="ghost" size="sm" disabled={discardingDraft || applying || savingDraft}>
                            <WxbIcon name="refresh" size={14} /> 放弃草稿
                        </WxbButton>
                    </WxbPopconfirm>
                )}

                <WxbButton
                    variant="secondary"
                    size="sm"
                    onClick={handleSaveDraft}
                    disabled={!hasUnsavedEdits || savingDraft || applying || discardingDraft}
                >
                    <WxbIcon name="batch-record" size={14} />
                    {savingDraft ? '保存中...' : '保存草稿'}
                </WxbButton>

                {runStatus === 'APPLIED' && !hasPendingChanges ? (
                    <WxbButton size="sm" disabled>
                        <WxbIcon name="review-ok" size={14} /> 已应用
                    </WxbButton>
                ) : (
                    <WxbPopconfirm
                        title={runStatus === 'APPLIED' ? '确认覆盖已应用结果' : '确认应用排班结果'}
                        description={runStatus === 'APPLIED'
                            ? '将按当前页面结果原子覆盖已写入的排班，并保留锁定数据。是否继续？'
                            : (hasPendingChanges
                                ? '将按当前草稿或未保存修改原子写入排班，并保留锁定数据。是否继续？'
                                : '将把当前求解结果原子写入排班，并保留锁定数据。是否继续？')}
                        onConfirm={handleApplyResult}
                        okText="确认"
                        cancelText="取消"
                        okButtonProps={{ loading: applying }}
                    >
                        <WxbButton
                            size="sm"
                            disabled={
                                applying
                                || savingDraft
                                || discardingDraft
                            }
                        >
                            <WxbIcon name="release" size={14} /> {applying
                                ? '应用中...'
                                : (runStatus === 'APPLIED' ? '覆盖应用' : '应用排班')}
                            {hasUnsavedEdits && (
                                <span className="v4-pending-count">+{editHistory.length}改</span>
                            )}
                        </WxbButton>
                    </WxbPopconfirm>
                )}
            </div>
        </div>
    );

    return (
        <WxbDrawer
            title={drawerTitle}
            open={visible}
            onClose={requestClose}
            width="100vw"
            placement="right"
            maskClosable={false}
            closable={false}
            destroyOnClose
            className="v4-result-drawer"
        >
            <div className="v4-result-container">
                {loading ? (
                    <div className="v4-result-loading">
                        <WxbSpinner size={40} tip="正在加载结果..." />
                    </div>
                ) : error ? (
                    <WxbAlert variant="error" title="无法加载结果">{error}</WxbAlert>
                ) : data && kpiMetrics ? (
                    <>
                        {(precheckSummary?.issues.length
                            || validationErrors.length > 0
                            || validationWarnings.length > 0
                            || editDelta) && (
                            <div
                                className="v4-precheck-bar"
                                data-status={
                                    validationErrors.length > 0 || precheckSummary?.status === 'ERROR'
                                        ? 'ERROR'
                                        : 'WARNING'
                                }
                            >
                                <div className="v4-precheck-bar-left">
                                    <WarningOutlined />
                                    <span>
                                        {validationErrors.length > 0
                                            ? `当前排班有 ${validationErrors.length} 项业务风险提示，不影响应用`
                                            : precheckSummary?.status === 'ERROR'
                                                ? '求解预检存在错误提示，不影响应用'
                                                : validationWarnings.length > 0
                                                    ? `当前排班有 ${validationWarnings.length} 项人工调整风险，可继续应用`
                                                : precheckSummary?.issues.length
                                                ? `${precheckSummary.issues.length} 项求解预检提示`
                                                : '当前编辑尚未保存'}
                                    </span>
                                    {(validationErrors.length > 0
                                        || validationWarnings.length > 0
                                        || Boolean(precheckSummary?.issues.length)) && (
                                        <WxbButton
                                            variant="ghost"
                                            size="sm"
                                            className="v4-precheck-toggle"
                                            onClick={() => setPrecheckExpanded((expanded) => !expanded)}
                                        >
                                            {precheckExpanded ? '收起' : '查看详情'}
                                        </WxbButton>
                                    )}
                                </div>
                                {editDelta && (
                                    <div className="v4-precheck-bar-right">
                                        编辑：{editDelta.count} 处修改
                                        <WxbTooltip title="撤销上一步编辑">
                                            <WxbButton variant="ghost" size="sm" onClick={handleUndo}>
                                                <UndoOutlined /> 撤销
                                            </WxbButton>
                                        </WxbTooltip>
                                        <WxbButton
                                            variant="ghost"
                                            size="sm"
                                            disabled={editorState.future.length === 0}
                                            onClick={handleRedo}
                                        >
                                            重做
                                        </WxbButton>
                                        <WxbButton
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setHistoryVisible(true)}
                                        >
                                            <HistoryOutlined /> 记录
                                        </WxbButton>
                                    </div>
                                )}
                            </div>
                        )}

                        {precheckExpanded
                            && (validationErrors.length > 0 || validationWarnings.length > 0 || precheckSummary)
                            && (
                            <div className="v4-precheck-detail">
                                {validationErrors.map((issue, index) => (
                                    <div key={`validation-${index}`} className="v4-precheck-item" data-status="ERROR">
                                        <span className="v4-precheck-dot" />
                                        <span>{issue.message}</span>
                                    </div>
                                ))}
                                {validationWarnings.map((issue, index) => (
                                    <div key={`validation-warning-${index}`} className="v4-precheck-item" data-status="WARNING">
                                        <span className="v4-precheck-dot" />
                                        <span>{issue.message}</span>
                                    </div>
                                ))}
                                {precheckSummary?.issues.map((issue, index) => (
                                    <div key={`precheck-${index}`} className="v4-precheck-item" data-status={issue.status}>
                                        <span className="v4-precheck-dot" />
                                        <span>{issue.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {specialShiftSummary && (
                            <div
                                className="v4-special-shift-summary"
                                data-status={specialShiftSummary.shortage > 0 ? 'WARNING' : 'PASS'}
                            >
                                <strong>特殊班次联合约束</strong>
                                <span>{specialShiftSummary.requirementCount} 条规则</span>
                                <span>{specialShiftSummary.occurrenceCount} 个实例</span>
                                <span>
                                    已分配 {specialShiftSummary.assignedHeadcount}
                                    /{specialShiftSummary.requiredHeadcount} 人次
                                </span>
                                {specialShiftSummary.shortage > 0
                                    ? <WxbTag color="amber">欠配 {specialShiftSummary.shortage} 人次</WxbTag>
                                    : <WxbTag color="green">全部满足</WxbTag>}
                            </div>
                        )}

                        {/* ── KPI Row (compact inline) ── */}
                        <div className="v4-kpi-row">
                            <div className="v4-kpi-item">
                                <span className="v4-kpi-label">完成率</span>
                                <span className="v4-kpi-value" data-color={kpiMetrics.completion_rate === 100 ? 'green' : 'amber'}>
                                    {kpiMetrics.completion_rate}%
                                </span>
                            </div>
                            <div className="v4-kpi-item">
                                <span className="v4-kpi-label">覆盖度</span>
                                <span className="v4-kpi-value" data-color="blue">
                                    {kpiMetrics.coverage_rate}%
                                </span>
                            </div>
                            <div className="v4-kpi-item">
                                <span className="v4-kpi-label">均衡指数</span>
                                <span className="v4-kpi-value" data-color={kpiMetrics.balanceIndex >= 80 ? 'green' : 'amber'}>
                                    {kpiMetrics.balanceIndex}%
                                </span>
                            </div>
                            <div className="v4-kpi-item">
                                <span className="v4-kpi-label">参与</span>
                                <span className="v4-kpi-value">
                                    {kpiMetrics.employeeCount}人
                                </span>
                            </div>
                            <div className="v4-kpi-item">
                                <span className="v4-kpi-label">质量分</span>
                                <span className="v4-kpi-value" data-color={kpiMetrics.qualityScore >= 80 ? 'green' : kpiMetrics.qualityScore >= 60 ? 'amber' : 'red'}>
                                    {kpiMetrics.qualityScore}<span className="v4-kpi-denominator">/100</span>
                                </span>
                            </div>
                            <WxbButton
                                variant="ghost"
                                className="v4-kpi-item v4-kpi-item-action"
                                title="点击查看未覆盖操作清单"
                                onClick={() => {
                                    setActiveTab('assignments');
                                    setAssignFilterSignal(prev => ({
                                        status: 'UNASSIGNED', nonce: (prev?.nonce || 0) + 1,
                                    }));
                                }}>
                                <span className="v4-kpi-label">未覆盖操作</span>
                                <span className="v4-kpi-value" data-color={kpiMetrics.uncoveredOps > 0 ? 'red' : 'green'}>
                                    {kpiMetrics.uncoveredOps}
                                    {kpiMetrics.partialOps > 0 && (
                                        <span className="v4-kpi-partial">
                                            +{kpiMetrics.partialOps}部分
                                        </span>
                                    )}
                                </span>
                            </WxbButton>
                            {kpiMetrics.standaloneTotal > 0 && (
                                <div className="v4-kpi-item">
                                    <span className="v4-kpi-label"><ToolOutlined />独立任务</span>
                                    <span className="v4-kpi-value" data-color={kpiMetrics.standaloneAssigned === kpiMetrics.standaloneTotal ? 'green' : 'amber'}>
                                        {kpiMetrics.standaloneAssigned}/{kpiMetrics.standaloneTotal}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* ── View Tab Switcher ── */}
                        <WxbSegmented
                            className="v4-view-tabs"
                            value={activeTab}
                            onChange={(value) => setActiveTab(value as 'matrix' | 'assignments')}
                            options={[
                                { value: 'matrix', label: '排班矩阵' },
                                {
                                    value: 'assignments',
                                    label: (
                                        <span className="v4-view-tab-label">
                                            操作分配明细
                                            {kpiMetrics.totalPositions - kpiMetrics.assignedPositions > 0 && (
                                                <span className="v4-view-tab-badge">
                                                    {kpiMetrics.totalPositions - kpiMetrics.assignedPositions}
                                                </span>
                                            )}
                                        </span>
                                    ),
                                },
                            ]}
                        />

                        {activeTab === 'matrix' ? (
                            <>
                                {/* ── Schedule Matrix (core) ── */}
                                <ScheduleMatrix
                                    shiftAssignments={data.shift_assignments || []}
                                    assignments={data.assignments || []}
                                    calendarDays={data.calendar_days || []}
                                    operations={data.operations}
                                    onEditShift={setEditTarget}
                                />

                                {/* ── Edit Stats Footer ── */}
                                {editDelta && (
                                    <div className="v4-edit-footer">
                                        <div className="v4-edit-footer-stats">
                                            班次变更:
                                            {editDelta.nightDelta !== 0 && (
                                                <span className={editDelta.nightDelta > 0 ? 'is-warning' : 'is-success'}>
                                                    {editDelta.nightDelta > 0 ? '+' : ''}{editDelta.nightDelta}夜班
                                                </span>
                                            )}
                                            {editDelta.hoursDelta !== 0 && (
                                                <span>
                                                    工时偏差Δ{editDelta.hoursDelta > 0 ? '+' : ''}{editDelta.hoursDelta}h
                                                </span>
                                            )}
                                            <span className="is-muted">共{editDelta.count}处修改</span>
                                        </div>
                                        <div className="v4-edit-footer-actions">
                                            <WxbPopconfirm
                                                title="重置所有编辑"
                                                description="将恢复到当前加载的服务器版本，所有未保存修改将丢失。"
                                                onConfirm={handleResetAll}
                                                okText="确认重置"
                                                cancelText="取消"
                                            >
                                                <WxbButton variant="ghost" size="sm">
                                                    重置所有
                                                </WxbButton>
                                            </WxbPopconfirm>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <AssignmentsView
                                operations={data.operations || []}
                                shiftAssignments={data.shift_assignments || []}
                                shiftOptions={shiftOptions}
                                calendarDays={data.calendar_days || []}
                                employeeMeta={data.employee_meta || {}}
                                externalFilter={assignFilterSignal}
                                enableUniqueEmployee={data.constraint_config?.enable_unique_employee !== false}
                                onAssign={handleAssign}
                                onUnassign={handleUnassign}
                                onAssignWithShiftChange={handleAssignWithShiftChange}
                            />
                        )}
                    </>
                ) : null}
            </div>

            {/* Manual Edit Drawer */}
            <ManualEditDrawer
                visible={!!editTarget}
                shiftAssignment={editTarget}
                allShiftAssignments={data?.shift_assignments || []}
                shiftOptions={shiftOptions}
                eligibleShiftIds={editTarget
                    ? data?.employee_meta?.[editTarget.employee_id]?.eligible_shift_ids
                    : undefined}
                onClose={() => setEditTarget(null)}
                onApplyEdit={handleApplyEdit}
            />

            {/* Edit History Drawer */}
            <WxbDrawer
                title={`编辑记录 (${editHistory.length})`}
                open={historyVisible}
                onClose={() => setHistoryVisible(false)}
                width={400}
                placement="right"
            >
                {editHistory.length === 0 ? (
                    <WxbEmpty description="暂无编辑记录" />
                ) : (
                    <div className="v4-history-list">
                        {editHistory.map((entry, i) => (
                            <div key={entry.id} className="v4-history-item">
                                <WxbTag color="blue">变更 {i + 1}</WxbTag>
                                <span>{entry.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </WxbDrawer>

            <WxbModal
                title="存在未保存修改"
                open={closeConfirmVisible}
                onCancel={() => setCloseConfirmVisible(false)}
                footer={(
                    <div className="v4-modal-actions">
                        <WxbButton variant="ghost" onClick={() => setCloseConfirmVisible(false)}>继续编辑</WxbButton>
                        <WxbButton
                            variant="danger"
                            onClick={() => {
                                setCloseConfirmVisible(false);
                                onClose();
                            }}
                        >
                            放弃修改并关闭
                        </WxbButton>
                    </div>
                )}
            >
                当前有 {editHistory.length} 项未保存修改，关闭后将丢失。
            </WxbModal>
        </WxbDrawer>
    );
};

export default SolveResultV4Page;
